/**
 * Aktionsprotokoll-Adapter (CRM 2.0, v2 admin-scoped)
 *
 * Backend-Vertrag (alles im `{ data, meta, errors }`-Envelope der v2-API,
 * Middleware `permission:protokoll.read`):
 *
 *   GET /v2/admin/activity-protocol          — paginierte Liste, Filter s. Query
 *   GET /v2/admin/activity-protocol/options  — Filter-Optionen (Aktionen, Admins, …)
 *
 * Profil-Modus: `user_id` liefert nur Einträge, die zu diesem CRM-Kontakt
 * (User + alle seine Profile) gehören. Die Zuordnung passiert serverseitig.
 */

import { apiV2 } from './api';
import type { V2Meta, V2Paginated } from './familyAccounts';

/* ─── Domänen-Typen ─────────────────────────────────────────────────────── */

export type ActivityActorType = 'admin' | 'user' | 'system';
export type ActivityContext = 'pflege' | 'betreuung';
export type ActivityEntityType = 'pb' | 'bk';

export interface ActivityProtocolEntry {
  id: number;
  actor_type: ActivityActorType;
  actor_id: number | null;
  /** Aufgelöster Name (Admin: Vor-/Nachname, User: Profilname, System: "System"). */
  actor_name: string | null;
  method: string | null;
  route: string;
  context: ActivityContext | null;
  /** Erste numerische ID im Pfad (Profil- ODER User-ID, je nach Route). */
  entity_id: number | null;
  entity_type: ActivityEntityType | null;
  entity_label: string | null;
  /** users.id des betroffenen Kontakts, wenn auflösbar. */
  entity_user_id?: number | null;
  /** Familienkonto (users.konto) für PB-Links: /pflege/{konto}/{profile_id}. */
  entity_konto?: number | null;
  entity_profile_id?: number | null;
  /** create|update|delete|login|3cx_call_<status>|… */
  action: string;
  /**
   * Modul-Schlüssel (task, note, care, calendar …, s. `PROTOCOL_MODULES`).
   * Neue Einträge tragen es in meta.module; die API leitet es für ältere
   * Einträge aus Action/Route ab. null = nicht zuordenbar.
   */
  module?: string | null;
  meta: Record<string, unknown> | null;
  /** ISO-8601 */
  created_at: string;
}

export interface ActivityProtocolQuery {
  actor_type?: ActivityActorType | '' | null;
  actor_id?: number | null;
  context?: ActivityContext | '' | null;
  /** Einzelwert oder kommagetrennte Liste, Präfix-Match mit `*` (z. B. `3cx_call_*`). */
  action?: string | null;
  /** Einzelnes Modul oder kommagetrennte Liste (s. `PROTOCOL_MODULES`). */
  module?: string | null;
  /** true: Hintergrund-Aktivität (meta.quiet: Chat-Polling, UI-Einstellungen) ausblenden. */
  hide_quiet?: boolean | null;
  entity_id?: number | null;
  route?: string | null;
  search?: string | null;
  /** Datum oder Datetime; `to` ist bei reinem Datum inklusiv bis Tagesende. */
  from?: string | null;
  to?: string | null;
  /** Profil-Modus: nur Einträge zu diesem CRM-Kontakt. */
  user_id?: number | null;
  page?: number;
  per_page?: number;
}

export interface ActivityProtocolOptions {
  actions: string[];
  admins: { id: number; name: string }[];
  actor_types: ActivityActorType[];
  contexts: ActivityContext[];
  /** Alle Modul-Schlüssel, die die API kennt (ältere API-Stände: fehlt → Fallback `PROTOCOL_MODULES`). */
  modules?: string[];
}

/** Modul-Schlüssel in Anzeigereihenfolge (Übersetzung: protokoll.module.<key>). */
export const PROTOCOL_MODULES = [
  'task', 'note', 'care', 'absence', 'pb', 'bk', 'profile', 'contact', 'family_account',
  'wiedervorlage', 'request', 'phase', 'calendar', 'documents', 'messages', 'schedule',
  'time_entries', 'leistungsnachweis', 'prescription', 'prices', 'rating', 'templates', 'mail',
  'admins', 'ai', 'settings', 'preferences', 'telephony', 'export', 'account', 'system', 'other',
] as const;

export type { V2Meta as ActivityProtocolMeta };

/* ─── Query-Serialisierung ──────────────────────────────────────────────── */

/**
 * Entfernt leere Werte (undefined, null, '' bzw. nur Whitespace) und trimmt
 * Strings, damit die Query-URL nur gesetzte Filter enthält.
 */
export function buildActivityProtocolParams(query: ActivityProtocolQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, raw] of Object.entries(query)) {
    if (raw === undefined || raw === null) continue;
    if (typeof raw === 'string') {
      const v = raw.trim();
      if (v === '') continue;
      params[key] = v;
    } else if (typeof raw === 'number') {
      if (Number.isNaN(raw)) continue;
      params[key] = raw;
    } else if (typeof raw === 'boolean') {
      if (raw) params[key] = 1; // false = Filter nicht gesetzt
    }
  }
  return params;
}

/* ─── Endpoints ──────────────────────────────────────────────────────────── */

/** GET /v2/admin/activity-protocol — paginierte Protokoll-Liste. */
export async function listActivityProtocol(
  query: ActivityProtocolQuery,
): Promise<V2Paginated<ActivityProtocolEntry>> {
  const res = await apiV2.get<{ data: ActivityProtocolEntry[] | null; meta: V2Meta }>(
    '/admin/activity-protocol',
    { params: buildActivityProtocolParams(query) },
  );
  return { data: res.data.data ?? [], meta: res.data.meta };
}

/** GET /v2/admin/activity-protocol/options — Filter-Optionen für die Ansicht. */
export async function getActivityProtocolOptions(): Promise<ActivityProtocolOptions> {
  const res = await apiV2.get<{ data: ActivityProtocolOptions }>('/admin/activity-protocol/options');
  return res.data.data;
}

/** GET /v2/admin/activity-protocol/pb-ticker — jüngste fachliche Änderung je PB. */
export async function listPbTicker(limit = 20): Promise<ActivityProtocolEntry[]> {
  const res = await apiV2.get<{ data: ActivityProtocolEntry[] | null }>(
    '/admin/activity-protocol/pb-ticker',
    { params: { limit } },
  );
  return res.data.data ?? [];
}

/* ─── Lesbare Sätze (Feedback 11.10.2026) ───────────────────────────────── */

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Fachliche Actions des ActivityProtocolObserver (API) mit eigenem Satzbaustein. */
export const DESCRIBED_ACTIONS = [
  'note_created', 'note_updated', 'note_deleted', 'note_pinned', 'note_unpinned',
  'task_created', 'task_updated', 'task_done', 'task_reopened', 'task_deleted',
  'care_created', 'care_updated', 'care_deleted',
  'absence_created', 'absence_updated', 'absence_deleted',
  'profile_created', 'profile_updated', 'profile_deleted',
] as const;

const pad2 = (n: number) => String(n).padStart(2, '0');

function str(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.trim() === '' ? null : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** 'YYYY-MM-DD…' → 'DD.MM.YYYY', alles andere unverändert. */
function displayValue(v: unknown): string | null {
  const s = str(v);
  if (s === null) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T]00:00:00(?:\.0+)?Z?)?$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

function range(meta: Record<string, unknown>, tr: Translate): string {
  const from = displayValue(meta.startdatum) ?? '?';
  const to = displayValue(meta.enddatum);
  return to ? tr('protokoll.value.range', { from, to }) : tr('protokoll.value.from', { from });
}

/** "calendar_category" → "Calendar category" — lesbarer Fallback statt des rohen Schlüssels. */
export function humanizeKey(key: string): string {
  const t = key.replace(/_+/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function fieldLabel(field: string, tr: Translate): string {
  return tr(`protokoll.field.${field}`, { defaultValue: humanizeKey(field) });
}

/** Objekt-Bezeichnung mit unbestimmtem Artikel ("eine Aufgabe"); unbekannte Typen lesbar. */
export function objectLabel(object: string, tr: Translate): string {
  return tr(`protokoll.object.${object}`, { defaultValue: humanizeKey(object) });
}

/** Im Satz angezeigte Felder/Zeichen — die volle Liste steht in den aufgeklappten Details. */
const SENTENCE_MAX_FIELDS = 4;
const SENTENCE_MAX_VALUE = 50;

function shortValue(v: unknown, tr: Translate): string | null {
  if (v === true) return tr('protokoll.value.yes');
  if (v === false) return tr('protokoll.value.no');
  const s = displayValue(v);
  return s !== null && s.length > SENTENCE_MAX_VALUE ? `${s.slice(0, SENTENCE_MAX_VALUE - 1)}…` : s;
}

/** Feldänderungen als „Feld: alt → neu", Felder ohne Wert nur mit Namen (gekürzt auf wenige Felder). */
export function describeChanges(changes: unknown, tr: Translate): string {
  if (!changes || typeof changes !== 'object') return '';
  const empty = tr('protokoll.value.empty');
  const entries = Object.entries(changes as Record<string, unknown>);
  const parts = entries.slice(0, SENTENCE_MAX_FIELDS).map(([field, pair]) => {
    const label = fieldLabel(field, tr);
    if (!Array.isArray(pair)) return label;
    const [oldV, newV] = pair;
    return `${label}: ${shortValue(oldV, tr) ?? empty} → ${shortValue(newV, tr) ?? empty}`;
  });
  const more = entries.length - SENTENCE_MAX_FIELDS;
  if (more > 0) parts.push(tr('protokoll.changes.summary_more', { count: more }));
  return parts.join(', ');
}

/* ─── Änderungsliste (Aktionsprotokoll-Details, 12.10.2026) ──────────────── */

export interface ProtocolChangeField {
  field: string;
  old: unknown;
  new: unknown;
  /** true: der Wert darf laut API nicht protokolliert werden (nur Feldname). */
  hidden: boolean;
}

export interface ProtocolChangeGroup {
  module: string | null;
  object: string | null;
  objectId: number | null;
  label: string | null;
  event: 'created' | 'updated' | 'deleted' | null;
  fields: ProtocolChangeField[];
}

function toFields(changes: unknown): ProtocolChangeField[] {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) return [];
  return Object.entries(changes as Record<string, unknown>).map(([field, pair]) =>
    Array.isArray(pair)
      ? { field, old: pair[0] ?? null, new: pair[1] ?? null, hidden: false }
      : { field, old: null, new: null, hidden: true });
}

function eventOf(raw: unknown, action: string): ProtocolChangeGroup['event'] {
  if (raw === 'created' || raw === 'updated' || raw === 'deleted') return raw;
  if (/(^|_)create(d)?$/.test(action)) return 'created';
  if (/(^|_)delete(d)?$/.test(action)) return 'deleted';
  if (/(^|_)(update(d)?|done|reopened|pinned|unpinned)$/.test(action)) return 'updated';
  return null;
}

/**
 * Feldänderungen eines Eintrags als Gruppen (Hauptobjekt zuerst, danach
 * meta.related). Ältere Einträge ohne meta.changes → leere Liste.
 */
export function extractChangeGroups(e: Pick<ActivityProtocolEntry, 'action' | 'meta' | 'module'>): ProtocolChangeGroup[] {
  const meta = (e.meta ?? {}) as Record<string, unknown>;
  const groups: ProtocolChangeGroup[] = [];

  const main = toFields(meta.changes);
  if (main.length > 0) {
    groups.push({
      module: typeof meta.module === 'string' ? meta.module : (e.module ?? null),
      object: typeof meta.object === 'string' ? meta.object : null,
      objectId: typeof meta.object_id === 'number' ? meta.object_id : null,
      label: str(meta.label) ?? str(meta.title) ?? str(meta.name),
      event: eventOf(meta.event, e.action),
      fields: main,
    });
  }

  if (Array.isArray(meta.related)) {
    for (const r of meta.related as Array<Record<string, unknown>>) {
      const fields = toFields(r?.changes);
      if (fields.length === 0) continue;
      groups.push({
        module: typeof r.module === 'string' ? r.module : null,
        object: typeof r.object === 'string' ? r.object : null,
        objectId: typeof r.object_id === 'number' ? r.object_id : null,
        label: str(r.label),
        event: eventOf(r.event, ''),
        fields,
      });
    }
  }

  return groups;
}

/** Anzeigewert eines Feldes (Datum DD.MM.YYYY, bool Ja/Nein, leer → „leer"). */
export function formatChangeValue(v: unknown, tr: Translate): string {
  if (v === true) return tr('protokoll.value.yes');
  if (v === false) return tr('protokoll.value.no');
  return displayValue(v) ?? tr('protokoll.value.empty');
}

/** „Feld: alt → neu" bzw. bei Anlage/Löschung nur der angelegte/gelöschte Wert. */
export function formatChangeField(f: ProtocolChangeField, event: ProtocolChangeGroup['event'], tr: Translate): string {
  const label = fieldLabel(f.field, tr);
  if (f.hidden) return `${label}: ${tr('protokoll.changes.hidden')}`;
  if (event === 'created') return `${label}: ${formatChangeValue(f.new, tr)}`;
  if (event === 'deleted') return `${label}: ${formatChangeValue(f.old, tr)}`;
  return `${label}: ${formatChangeValue(f.old, tr)} → ${formatChangeValue(f.new, tr)}`;
}

/** Kurzfassung für die eingeklappte Zeile: höchstens `max` Felder. */
export function summarizeChanges(groups: ProtocolChangeGroup[], tr: Translate, max = 3): string {
  const g = groups[0];
  if (!g) return '';
  const parts = g.fields.slice(0, max).map(f => formatChangeField(f, g.event, tr));
  const more = g.fields.length - max;
  if (more > 0) parts.push(tr('protokoll.changes.summary_more', { count: more }));
  return parts.join('; ');
}

/** Modul-Bezeichnung (übersetzt); unbekannte Schlüssel erscheinen unverändert. */
export function moduleLabel(key: string | null | undefined, tr: Translate): string {
  if (!key) return '';
  return tr(`protokoll.module.${key}`, { defaultValue: key });
}

/**
 * Satz für einen Protokolleintrag, z. B.
 * „Max Muster hat um 10:23 Uhr am 07.10.2026 bei Erika Muster eine Notiz
 * erstellt mit dem Text: „eine neue Notiz““.
 *
 * `withPerson` = false lässt „bei …" weg (Profil-Protokoll, Ticker-Zeile mit
 * eigenem Namen).
 *
 * `brief` = true bildet den Satz ohne Inhalte (Notiztext, Titel, Werte) — für
 * den PB-Ticker, dessen API nur Objekt + Feldnamen (`meta.fields`) liefert.
 */
export function describeEntry(e: ActivityProtocolEntry, tr: Translate, withPerson = true, brief = false): string {
  const d = new Date(e.created_at);
  const valid = !Number.isNaN(d.getTime());
  const date = valid ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}` : e.created_at;
  const time = valid ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
  const actor = e.actor_name
    ?? (e.actor_type === 'system' ? tr('protokoll.actor_type.system') : tr('protokoll.sentence.unknown_actor'));

  const meta = (e.meta ?? {}) as Record<string, unknown>;
  const vars: Record<string, unknown> = {
    text: str(meta.text_excerpt) ?? '',
    title: str(meta.title) ?? '',
    range: range(meta, tr),
    changes: describeChanges(meta.changes, tr),
    grund: str(meta.grund) ?? '',
  };

  let what: string;
  if (brief && (DESCRIBED_ACTIONS as readonly string[]).includes(e.action)) {
    what = tr(`protokoll.brief.${e.action}`);
    const fields = Array.isArray(meta.fields)
      ? (meta.fields as unknown[]).map(f => fieldLabel(String(f), tr)).join(', ')
      : '';
    if (fields !== '') what = tr('protokoll.brief.with_fields', { what, fields });
  } else if ((DESCRIBED_ACTIONS as readonly string[]).includes(e.action)) {
    const hasChanges = typeof vars.changes === 'string' && vars.changes !== '';
    const key = e.action.endsWith('_updated') && !hasChanges ? `${e.action}_plain` : e.action;
    what = tr(`protokoll.what.${key}`, vars);
  } else if (
    ['create', 'update', 'delete'].includes(e.action) && typeof meta.object === 'string' && e.module
  ) {
    // Generischer Eintrag mit Objekt (Aktionsprotokoll-Details): „im Modul Kalender
    // eine Kalender-Kategorie „Arzt“ angelegt“.
    const type = objectLabel(meta.object, tr);
    const lbl = str(meta.label);
    const oid = typeof meta.object_id === 'number' ? meta.object_id : null;
    // Nie Freitext: ohne Namens-Label nennt der Satz Typ und ID.
    const object = lbl ? tr('protokoll.object.with_label', { type, label: lbl })
      : oid !== null ? tr('protokoll.object.with_id', { type, id: oid }) : type;
    const hasChanges = vars.changes !== '';
    const key = e.action === 'update' ? (hasChanges && !brief ? 'generic_update_changes' : 'generic_update')
      : e.action === 'create' ? 'generic_create' : 'generic_delete';
    what = tr(`protokoll.what.${key}`, { module: moduleLabel(e.module, tr), object, changes: vars.changes });
  } else {
    const label = e.action.startsWith('3cx_call_')
      ? tr(`protokoll.call.status.${e.action.slice('3cx_call_'.length)}`, { defaultValue: e.action })
      : tr(`protokoll.action.${e.action}`, { defaultValue: e.action });
    what = tr('protokoll.what.generic', { action: label });
  }

  const person = withPerson ? e.entity_label : null;
  return person
    ? tr('protokoll.sentence.with_person', { actor, time, date, person, what })
    : tr('protokoll.sentence.without_person', { actor, time, date, what });
}
